import { useEffect, useState } from 'react';

export enum ScriptStatus {
  Idle = 'IDLE',
  Loading = 'LOADING',
  Ready = 'READY',
  Error = 'ERROR',
}

export interface Options {
  id?: string;
}

/** @see https://usehooks.com/useScript/ */
export function useScript(src: string | undefined, { id }: Options = {}): ScriptStatus {
  // Keep track of script status
  const [status, setStatus] = useState<ScriptStatus>(src ? ScriptStatus.Loading : ScriptStatus.Idle);
  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
      if (!src) {
        setStatus(ScriptStatus.Idle);
        return;
      }
      // Fetch existing script element by src
      // It may have been added by another intance of this hook
      let script: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`);
      if (!script) {
        // Create script
        script = document.createElement('script');
        script.src = src;
        script.async = true;

        if (id) {
          script.id = id;
        }

        script.setAttribute('data-status', ScriptStatus.Loading);
        // Add script to document body
        document.body.appendChild(script);
        // Store status in attribute on script
        // This can be read by other instances of this hook
        const setAttributeFromEvent = (event: Event) => {
          script?.setAttribute('data-status', event.type === 'load' ? ScriptStatus.Ready : ScriptStatus.Error);
        };
        script.addEventListener('load', setAttributeFromEvent);
        script.addEventListener('error', setAttributeFromEvent);
      } else {
        // Grab existing script status from attribute and set to state.
        setStatus(script.getAttribute('data-status') as ScriptStatus);
      }
      // Script event handler to update status in state
      // Note: Even if the script already exists we still need to add
      // event handlers to update the state for *this* hook instance.
      const setStateFromEvent = (event: Event) => {
        setStatus(event.type === 'load' ? ScriptStatus.Ready : ScriptStatus.Error);
      };
      // Add event listeners
      script.addEventListener('load', setStateFromEvent);
      script.addEventListener('error', setStateFromEvent);
      // Remove event listeners on cleanup
      // eslint-disable-next-line consistent-return
      return () => {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      };
    },
    [src, id], // Only re-run effect if script src changes
  );

  return status;
}
